/** COMPANY EXPERTS (EXPERT-ROOMS §14/§15) — GET /tenants/{tid}/company-experts: what the company shares, this
 *  workspace's subscriptions, and (owners/admins) production releases waiting for a second person. */
import { getRestApiBearerToken } from './apiToken.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/** ``opts.q`` = catalog search (EXPERT-ROOMS §18); ``opts.detail`` = {expertTenantId, expertNetworkId} → that one's
 *  full listing (teams + rules, skills, agents, needs, fact kinds, notes) on its row. */
export async function getCompanyExperts(tenantId, opts = {}) {
  if (!tenantId) return {};
  const qs = new URLSearchParams();
  if (opts.q) qs.set('q', opts.q);
  if (opts.detail?.expertNetworkId) {
    qs.set('expertTenantId', opts.detail.expertTenantId || '');
    qs.set('expertNetworkId', opts.detail.expertNetworkId);
  }
  const url = `${API_BASE_URL}/tenants/${encodeURIComponent(tenantId)}/company-experts${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getRestApiBearerToken()}` } });
  if (!res.ok) return {};
  return res.json().catch(() => ({}));
}

async function putCompanyExperts(tenantId, body) {
  const res = await fetch(`${API_BASE_URL}/tenants/${encodeURIComponent(tenantId)}/company-experts`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || `HTTP ${res.status}`);
    err.fieldErrors = data.fieldErrors || null;          // §14b — a subscription form's answers that need a look
    throw err;
  }
  return data;
}

/** ``action`` = subscribe | unsubscribe (workspace OWNER / ADMIN). → the catalog view. ``profile`` = ``{name, values}``
 *  — the answers to the expert's SUBSCRIPTION FORM (EXPERT-ROOMS §14b), required when it has one. A refusal carries
 *  ``err.fieldErrors`` = ``{fieldId: why}``. */
export async function setCompanyExpertSubscription(tenantId, action, { expertTenantId, expertNetworkId }, profile = null) {
  return putCompanyExperts(tenantId, { action, expertTenantId, expertNetworkId, ...(profile ? { profile } : {}) });
}

/** §14b — a SUBSCRIPTION PROFILE (named answers to the expert's form; they stay in this workspace). ``profile`` =
 *  ``{profileId?, name, values}`` (no profileId = a new one). */
export async function saveExpertProfile(tenantId, { expertTenantId, expertNetworkId }, profile) {
  return putCompanyExperts(tenantId, { action: 'profileSave', expertTenantId, expertNetworkId, profile });
}

export async function deleteExpertProfile(tenantId, { expertTenantId, expertNetworkId }, profileId) {
  return putCompanyExperts(tenantId, { action: 'profileDelete', expertTenantId, expertNetworkId, profileId });
}

/** THE PROVIDER decides a workspace's subscription to one of ITS experts (EXPERT-ROOMS §14a) — ``decision`` =
 *  approve | decline (decline = revoke). The network's creator or a workspace admin; the server checks. */
export async function decideExpertSubscription(expertNetworkId, tenantId, decision) {
  const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(expertNetworkId)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ companySharing: { action: decision === 'approve' ? 'approve' : 'revoke', tenantId } }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
}
